// A card payment's amount with its percentage card fee taken back out.
// Cash and fee-less payments count in full. The tip doesn't pay down the
// sale (and carries no card fee), so it's taken out first.
export function paymentBaseCents(t: any): number {
    const total = (t.totalCents || t.total_cents || 0) - (t.tipsCents || t.tips_cents || 0);
    const method = t.paymentMethod ?? t.payment_method;
    const feeType = t.feeType ?? t.fee_type;
    const basisPoint = Number(t.feeAmountInBasisPoint ?? t.fee_basis_point ?? t.feeBasisPoint ?? 0);
    if (method !== "CARD" || feeType !== "PERCENTAGE" || basisPoint <= 0) return total;
    return Math.round((total * 10000) / (10000 + basisPoint));
}

export function getSalePaymentBalances(sale: any) {
    if (!sale) {
        return {
            paidCents: 0,
            paidBaseCents: 0,
            remainingCents: 0,
            fullTotalCents: 0,
            paidTipsCents: 0,
            hasPriorPayment: false,
        };
    }

    const txs = (sale.paymentTransactions || sale.payment_transactions || [])
        .filter((t: any) => !(t.preAuth || t.pre_auth));
    // Money actually received, card fees included.
    const paidCents = txs.reduce(
        (sum: number, t: any) => sum + (t.totalCents || t.total_cents || 0),
        0
    );
    // The sale total excludes the dual-pricing card fee (it is added per
    // card payment), so measure what's been covered by each payment's pre-fee
    // amount. Otherwise a fee-inclusive deposit leaves too little owed and
    // the card fee on the balance is lost.
    const paidBaseCents = txs.reduce(
        (sum: number, t: any) => sum + paymentBaseCents(t),
        0
    );
    // Tips riding on those payments - received, but not toward the sale.
    const paidTipsCents = txs.reduce(
        (sum: number, t: any) => sum + (t.tipsCents || t.tips_cents || 0),
        0
    );
    const saleTotal = sale.totalCents ?? sale.total_cents ?? 0;
    const saleSubtotal = sale.subtotalCents ?? sale.subtotal_cents ?? 0;
    const fullTotalCents = saleTotal > 0 ? saleTotal : saleSubtotal;
    // Owed before any card fee: cash pays exactly this, card adds the fee.
    // A closed sale is settled. This also matters because a walk-in sale paid
    // by card in the portal stores its total WITH the fee, which would
    // otherwise show the fee as a phantom balance.
    // Refunded and voided sales are finished too: nothing is owed on them.
    const isSettled = ["CLOSED", "REFUNDED", "VOID"].includes(String(sale.status ?? "").toUpperCase());
    const remainingCents = isSettled ? 0 : Math.max(0, fullTotalCents - paidBaseCents);

    return {
        paidCents,
        paidBaseCents,
        remainingCents,
        fullTotalCents,
        paidTipsCents,
        hasPriorPayment: paidCents > 0,
    };
}
